/**
 * FP3-028 — Frame Practice auth context.
 *
 * Loads the current user via /auth/me on app mount and exposes
 * { user, provider, organization, loading, refresh, logout, login }.
 * Routes wrap their content in <ProtectedRoute /> to redirect to
 * /login when no session exists. Admin-only views additionally check
 * user.permission_role.
 *
 * Invite-only model: there is no signup helper here. New accounts only
 * arrive via /invites/accept (handled on the dedicated accept page).
 *
 * FP3-029B4 — When an admin family user opens a provider workspace
 * via `?as_provider={providerId}`, the auth context resolves a
 * `shadowProvider` from /admin/providers/{id} and substitutes it
 * into the `provider` slot. This way every page that reads
 * `useAuth().provider?.id` (Today, Caseload, Schedule, Reports,
 * Notifications, Live Session) automatically scopes to the selected
 * provider — no fall-back to the legacy seeded `is_current=True`
 * row.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import api from "@/lib/api";

const AuthContext = createContext(null);

const ADMIN_FAMILY = ["admin", "org_admin", "super_admin"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [provider, setProvider] = useState(null);
  const [organization, setOrganization] = useState(null);
  // FP3-062 follow-up (Jun 1, 2026) — Surface the backend's
  // `is_solo_provider` flag in the auth context so every capability
  // gate (CaseloadList bulk affordance, StudentWorkspace rename,
  // WorkspaceInfo solo-only controls, Import My Caseload, …) can
  // honour the SAME rule the backend uses (user.account_type OR
  // organization.account_type === "individual_provider"). Without
  // this, real solo providers created via the Request Access flow
  // — whose user row carries an empty `account_type` even though
  // their org row is marked solo — silently fail every UI gate.
  const [isSoloProvider, setIsSoloProvider] = useState(false);
  // FP3-079D follow-up — Ghost-aware owner gate computed by the
  // BACKEND (/auth/me): supervision only restricts owner controls
  // when the supervising provider is a real, NAMED record. Nameless
  // or missing supervisor pointers never demote a solo workspace
  // owner. `null` until /auth/me resolves; local fallback below.
  const [ownerGate, setOwnerGate] = useState(null);
  const [personalStatus, setPersonalStatus] = useState("");
  const [shadowProvider, setShadowProvider] = useState(null);
  // FP3-046 — Terms of Use acceptance pointer surfaced by /auth/me.
  // The FE route guard reads this to decide whether to redirect a
  // freshly-authenticated user to /terms.
  const [termsStatus, setTermsStatus] = useState(null);
  // FP3-078 — Student-data authorization checkpoint status. Surfaced
  // by /auth/me so the FE can decide whether to show the one-time
  // acknowledgment modal before Add Student / Import. Separate from
  // termsStatus by design (paid customer agreement is parked).
  const [studentDataAckStatus, setStudentDataAckStatus] = useState(null);
  const [studentDataAckPending, setStudentDataAckPending] = useState(null);
  // FP3-075 — Resolved account preferences from /auth/me:
  //   { ui_mode: "simple" | "advanced",
  //     fidelity_monitoring: "on" | "off" }
  // Always populated with backend-resolved defaults so consumers can
  // read `preferences.ui_mode` without null-checking.
  const [preferences, setPreferences] = useState({
    ui_mode: "advanced",
    fidelity_monitoring: "on",
  });
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const refresh = useCallback(async () => {
    try {
      const res = await api.authMe();
      setUser(res.data.user);
      setProvider(res.data.provider || null);
      setOrganization(res.data.organization || null);
      setIsSoloProvider(!!res.data.is_solo_provider);
      setOwnerGate(
        "is_solo_workspace_owner" in (res.data || {})
          ? {
              isOwner: !!res.data.is_solo_workspace_owner,
              supervised: !!res.data.has_active_supervision,
            }
          : null
      );
      setPersonalStatus(res.data.personal_status || "");
      setTermsStatus(res.data.terms_status || null);
      setStudentDataAckStatus(res.data.student_data_ack_status || null);
      setPreferences(
        res.data.preferences || {
          ui_mode: "advanced",
          fidelity_monitoring: "on",
        }
      );
    } catch (e) {
      setUser(null);
      setProvider(null);
      setOrganization(null);
      setIsSoloProvider(false);
      setOwnerGate(null);
      setPersonalStatus("");
      setTermsStatus(null);
      setStudentDataAckStatus(null);
      setPreferences({ ui_mode: "advanced", fidelity_monitoring: "on" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // FP3-029B4 — Resolve the shadow provider whenever an admin family
  // user navigates to a URL carrying `?as_provider=`. Loading the
  // shadow is cheap (single GET) and is naturally re-fetched if the
  // admin switches to a different provider via the URL.
  // FP3-029B6B — Supervising primary providers may also shadow into
  // their own active assistants. The frontend only attempts the
  // admin-detail call when the user is admin family; non-admin
  // supervising primaries fetch the much-cheaper public provider
  // record (`/providers/{id}`) and are guarded by the
  // /provider-messages endpoint's same supervision-group check on
  // any write paths. This keeps the shadow strictly read-oriented
  // for non-admins.
  const asProviderId = (() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("as_provider") || "";
  })();
  const isAdminFamily = !!user && ADMIN_FAMILY.includes(user.permission_role);

  useEffect(() => {
    if (!asProviderId) {
      setShadowProvider(null);
      return;
    }
    let cancelled = false;
    if (isAdminFamily) {
      api
        .getAdminProviderDetail(asProviderId)
        .then((r) => {
          if (cancelled) return;
          setShadowProvider(r.data || null);
        })
        .catch(() => {
          if (cancelled) return;
          setShadowProvider(null);
        });
    } else {
      // FP3-029B6B — Non-admin supervising-primary path. We only
      // load the bare provider record; UI surfaces gate reads/writes
      // upstream. The shadow is treated as read-only.
      api
        .getProviders()
        .then((r) => {
          if (cancelled) return;
          const p = (r.data || []).find((x) => x.id === asProviderId);
          setShadowProvider(p || null);
        })
        .catch(() => {
          if (cancelled) return;
          setShadowProvider(null);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [asProviderId, isAdminFamily]);

  // FP3-078 — Promise-based gate the call sites use to bracket
  // student-data-creating actions. Resolves immediately when the
  // current user has already acknowledged the latest version;
  // otherwise opens the modal and resolves only after the user
  // submits the acknowledgment.
  const ensureStudentDataAck = useCallback(async () => {
    // FP3-078B — Race-safe path: if the auth context hasn't
    // hydrated `studentDataAckStatus` yet (fresh signup just
    // landed at /caseload before /auth/me returned), fetch fresh
    // status before deciding whether to pop the modal. Without
    // this we used to return a never-resolving promise + the
    // modal silently skipped render because status was null.
    let status = studentDataAckStatus;
    if (!status) {
      try {
        const r = await api.getStudentDataAckStatus();
        status = r?.data || null;
        setStudentDataAckStatus(status);
      } catch {
        status = null;
      }
    }
    if (status && status.acknowledged) {
      return true;
    }
    return new Promise((resolve) => {
      setStudentDataAckPending({ resolve });
    });
  }, [studentDataAckStatus]);

  // FP3-078B — Expose a fire-and-forget "open the modal" trigger
  // that the global axios 403 interceptor uses. The interceptor
  // dispatches a window event; the modal listener calls this so
  // the auth-context pending slot is filled even when no React
  // call site invoked `ensureStudentDataAck()` first.
  const openStudentDataAck = useCallback(() => {
    setStudentDataAckPending((prev) => prev || { resolve: () => {} });
  }, []);

  // FP3-078B — Refresh ack status on demand. Used by the modal
  // when it pops via the global interceptor before /auth/me has
  // hydrated the slot.
  const refreshStudentDataAck = useCallback(async () => {
    try {
      const r = await api.getStudentDataAckStatus();
      setStudentDataAckStatus(r?.data || null);
    } catch {
      /* noop */
    }
  }, []);

  // FP3-078 — Called by StudentDataAckModal after the user submits
  // the acknowledgment. Resolves the pending promise so the
  // original call-site can re-fire the gated request.
  const completeStudentDataAck = useCallback(
    async (success) => {
      if (success) {
        try {
          const r = await api.getStudentDataAckStatus();
          setStudentDataAckStatus(r.data || null);
        } catch {
          /* noop — modal closes; gate retries on next attempt */
        }
      }
      const pending = studentDataAckPending;
      setStudentDataAckPending(null);
      if (pending?.resolve) pending.resolve(!!success);
    },
    [studentDataAckPending]
  );

  const login = useCallback(
    async (email, password) => {
      await api.authLogin({ email, password });
      await refresh();
    },
    [refresh]
  );

  const logout = useCallback(async () => {
    try {
      await api.authLogout();
    } catch (e) {
      // ignore — clearing local state below is what matters
    }
    setUser(null);
    setProvider(null);
    setOrganization(null);
    setShadowProvider(null);
    setPersonalStatus("");
    // FP3-029C-2 — Wipe module-level caches that key off the
    // signed-in user so a fast logout→login doesn't leak the
    // previous user's data into pickers.
    try {
      const mod = await import(
        "@/components/caseload/AssistantAssignmentChip"
      );
      mod.clearMyAssistantsCache?.();
    } catch {
      /* noop */
    }
    // FP3-119A — Clear the AdminNotificationsBell module-level TTL
    // cache so a rapid logout → login as a different user does not
    // reuse the previous admin's cached notification set.
    try {
      const mod = await import(
        "@/components/admin/AdminNotificationsBell"
      );
      mod.resetNotificationCache?.();
    } catch {
      /* noop */
    }
    // FP3-119B — Wipe the React Query cache. Every admin query key
    // is scoped to (user_id, organization_id, shadow_provider_id),
    // so a stale key can't be reused by a different user — but a
    // hard reset is a belt-and-braces guarantee no PII survives
    // logout.
    try {
      const mod = await import("@/lib/queryClient");
      mod.resetCacheOnAuthChange?.();
    } catch {
      /* noop */
    }
  }, []);

  // Effective provider — shadow wins for admin family in read-only
  // mode. Provider users always see their own provider profile.
  const effectiveProvider = shadowProvider || provider;

  // FP3-079C / FP3-079D — Solo workspace OWNER detection. Prefers
  // the backend-computed ghost-aware flags from /auth/me: an
  // "active" supervision relationship only restricts owner controls
  // when the supervising provider is a real, NAMED record. Nameless
  // or missing supervisor pointers (production data drift) never
  // demote a solo workspace owner. Provider type / provider_role_type
  // alone never restrict owner controls. Local fallback keeps the
  // supervision_status rule if the flags are ever absent.
  const isSupervisedAssistant = ownerGate
    ? ownerGate.supervised
    : (user?.supervision_status || "").toLowerCase() === "active";
  const isSoloWorkspaceOwner = ownerGate
    ? ownerGate.isOwner
    : (user?.permission_role || "").toLowerCase() === "provider"
      && !!isSoloProvider
      && !isSupervisedAssistant;

  return (
    <AuthContext.Provider
      value={{
        user,
        provider: effectiveProvider,
        // Surface the raw values too in case a page needs to
        // distinguish (e.g., the sidebar's "Read-only · Name" chip).
        ownProvider: provider,
        shadowProvider,
        asProviderId,
        organization,
        // FP3-062 follow-up — backend-computed solo flag, identical
        // to the `is_solo_provider_user` check used by every backend
        // gate. Use this in every UI capability check instead of
        // re-deriving from `user.account_type` (which is empty for
        // solo providers minted through the Request Access flow).
        isSoloProvider,
        // FP3-079C — True only for the solo workspace OWNER (any
        // discipline, incl. SLPA/COTA/PTA). Invited assistants with a
        // supervising provider are excluded. Use this for caseload
        // mutation gates instead of a blanket assistant check.
        isSoloWorkspaceOwner,
        isSupervisedAssistant,
        personalStatus,
        setPersonalStatus,
        // FP3-046 — Terms acceptance status; soft banner on stale
        // versions, but no auto-redirect (FP3-078 — softened so
        // existing users aren't hit with a fresh full-page wall on
        // login).
        termsStatus,
        refreshTermsStatus: refresh,
        // FP3-078 — Student-data authorization checkpoint state +
        // promise-based gate. Call sites do:
        //
        //   await ensureStudentDataAck();
        //   await api.createStudent(...);
        //
        // The gate resolves immediately when already acknowledged.
        studentDataAckStatus,
        studentDataAckPending,
        ensureStudentDataAck,
        completeStudentDataAck,
        // FP3-078B — Defense-in-depth: a global axios interceptor
        // can also open the modal directly (without an explicit
        // ensureStudentDataAck call) when a gated endpoint returns
        // 403 needs_student_data_ack. The modal calls these.
        openStudentDataAck,
        refreshStudentDataAck,
        // FP3-075 — Resolved account preferences (Simple/Advanced
        // Mode + Fidelity On/Off). Always populated with safe
        // defaults so consumers don't need null checks.
        preferences,
        loading,
        refresh,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div
        data-testid="auth-loading"
        className="h-screen w-screen flex items-center justify-center text-sm text-[#6B7280]"
      >
        Loading…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  // FP3-078 — Soft-gate. Previously (FP3-046) we hard-redirected
  // every un-accepted user to /terms on first protected-route hit.
  // Real-world feedback: that felt like a heavy legal wall the
  // moment a user signed in. We now keep /terms reachable as a
  // page + show a soft banner in the shell when the user is
  // stale, but DO NOT block the rest of the workspace. Signup
  // flows still require Terms + Privacy acceptance at the source
  // (StartPilot, request-access).
  if (adminOnly && !ADMIN_FAMILY.includes(user.permission_role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}
